/**
 * @file src/hooks/useOrganizationAccountPath.ts
 * @exports useOrganizationAccountPath
 * @summary Resolves missing organization short names through the authenticated API before organization navigation.
 */
import {
	OrganizationsDocument,
	type OrganizationsQuery,
	type OrganizationsQueryVariables,
} from "src/generated/graphql";
import { useFetchData } from "src/graphql/fetcher";
import { showError } from "src/helpers/dialog/showError";
import { paths } from "src/const/paths";

/** Returns an organization path resolver that queries by ID when no short name was supplied. */
export function useOrganizationAccountPath() {
	const fetchOrganizations = useFetchData<OrganizationsQuery, OrganizationsQueryVariables>(OrganizationsDocument);

	return async (organization: { id: string; shortName?: string | null }): Promise<string> => {
		try {
			let shortName = organization.shortName;
			if (!shortName) {
				const { organizations } = await fetchOrganizations({ where: { id: { equals: organization.id } } });
				shortName = organizations.find((org) => org.id === organization.id)?.shortName;
			}
			if (!shortName) throw new Error("Organization short name is unavailable");
			// Keep the reserved creation route distinct from an organization page.
			return `${paths.organizations}/${encodeURIComponent(shortName.toLowerCase() === "create" ? organization.id : shortName)}`;
		} catch {
			showError("Unable to open this organization. Please select an organization and try again.");
			return paths.organizations;
		}
	};
}
